// ============================================================================
// 花火玉の号数データ
// ----------------------------------------------------------------------------
// 打上高さ・開花直径・鑑賞距離の目安はユーザー提供の表に基づく。
// 号数ごとの解説（用途・使われ方）は下記を参考に一般的な傾向として要約した
// （自治体・煙火業者により保安距離の具体的な基準値は異なるため、ここでは
// 「どのような場面で使われる傾向があるか」という目安の説明に留める）：
//   - 佐藤煙火 玉の大きさ https://www.satoenka.jp/kind/size.html
//   - 全国花火データベース https://expo2021.jp/archives/245
//   - ハナビディア https://www.hanabidia.com/size-of-fireworks/
// ============================================================================

export interface ShellSize {
  /** 号数（尺玉=10号など、「尺」表記のものも号数に統一）。 */
  go: number;
  /** 表示用ラベル。 */
  label: string;
  /** 打上高さの目安(m)。 */
  altitude: number;
  /** 開花直径の目安。 */
  burstDiameter: string;
  /** 鑑賞最適距離の目安(m) [下限, 上限]。 */
  optimalDistanceM: [number, number];
  /** 肉眼で十分見える限界距離の目安(km) [下限, 上限]。 */
  visibleLimitKm: [number, number];
  /** 号数の解説（一般的な使われ方の傾向）。 */
  description: string;
}

export const SHELL_SIZES: ShellSize[] = [
  {
    go: 2.5,
    optimalDistanceM: [46, 220],
    label: "2.5号",
    altitude: 80,
    burstDiameter: "約50m",
    visibleLimitKm: [1, 2],
    description:
      "住宅密集地やごく小規模な催しで使われる最小クラス。保安距離が小さく済むため、会場が狭い場所でも打ち上げやすい。",
  },
  {
    go: 3,
    optimalDistanceM: [69, 330],
    label: "3号",
    altitude: 120,
    burstDiameter: "約80〜100m",
    visibleLimitKm: [2, 3],
    description:
      "市街地で会場が狭く、周辺の建物や音・煙への影響を抑える必要がある場合によく使われる号数。取り扱いやすくコストも抑えやすい。",
  },
  {
    go: 4,
    optimalDistanceM: [87, 413],
    label: "4号",
    altitude: 150,
    burstDiameter: "約120m",
    visibleLimitKm: [3, 5],
    description: "3号よりやや迫力を出したいが、会場（河川敷等）がまだ広くない大会向けの号数。",
  },
  {
    go: 5,
    optimalDistanceM: [115, 550],
    label: "5号",
    altitude: 200,
    burstDiameter: "約150〜180m",
    visibleLimitKm: [5, 8],
    description:
      "中規模大会の主力玉。迫力と保安距離のバランスが良く、都市部の大会でも比較的使いやすい号数。",
  },
  {
    go: 6,
    optimalDistanceM: [127, 605],
    label: "6号",
    altitude: 220,
    burstDiameter: "約180〜200m",
    visibleLimitKm: [7, 10],
    description: "中規模〜やや大規模な大会向け。5号と尺玉クラスの間を担う号数。",
  },
  {
    go: 7,
    optimalDistanceM: [144, 688],
    label: "7号",
    altitude: 250,
    burstDiameter: "約200〜240m",
    visibleLimitKm: [8, 12],
    description: "やや大規模な大会の主力玉の一つ。広めの河川敷・公園などで使われる。",
  },
  {
    go: 8,
    optimalDistanceM: [162, 770],
    label: "8号",
    altitude: 280,
    burstDiameter: "約250〜280m",
    visibleLimitKm: [10, 15],
    description: "大規模大会で尺玉と並んで使われることが多い号数。",
  },
  {
    go: 10,
    optimalDistanceM: [173, 825],
    label: "10号（尺玉）",
    altitude: 300,
    burstDiameter: "約300m",
    visibleLimitKm: [15, 20],
    description:
      "日本の花火大会で最も象徴的な大玉。広い河川敷・海岸など十分な保安距離を確保できる会場のクライマックス演目に使われることが多い。",
  },
  {
    go: 20,
    optimalDistanceM: [260, 1238],
    label: "20号（二尺玉）",
    altitude: 450,
    burstDiameter: "約450〜500m",
    visibleLimitKm: [25, 35],
    description:
      "尺玉を超える特大玉。極めて広大な会場が必要で、限られた大規模大会でのみ演目として使われる。",
  },
  {
    go: 30,
    optimalDistanceM: [346, 1650],
    label: "30号（三尺玉）",
    altitude: 600,
    burstDiameter: "約600m",
    visibleLimitKm: [35, 50],
    description:
      "国内最大級。片貝まつり（新潟県小千谷市）など、ごく一部の大会でのみ打ち上げられる特別な玉。",
  },
  {
    go: 40,
    optimalDistanceM: [433, 2063],
    label: "40号（四尺玉）",
    altitude: 750,
    burstDiameter: "約750m",
    visibleLimitKm: [50, 70],
    description:
      "世界最大級の花火玉。片貝まつりで数年に一度打ち上げられる程度の、極めて特殊なクラス。",
  },
];

/** 打上高さ(m)に最も近い号数エントリを返す。 */
export function nearestShellSize(altitude: number): ShellSize {
  let best = SHELL_SIZES[0];
  let bestDiff = Math.abs(best.altitude - altitude);
  for (const s of SHELL_SIZES) {
    const diff = Math.abs(s.altitude - altitude);
    if (diff < bestDiff) {
      best = s;
      bestDiff = diff;
    }
  }
  return best;
}

// SHELL_SIZESのvisibleLimitKmは、障害物・大気減衰のない理想的な条件での
// 上限値（地平線までの幾何学的な限界距離よりもさらに控えめではあるが、
// それでも実際の花火観覧の条件を反映していない）。花火大会は主に夏場の
// 高湿度・霞んだ夜、かつ日本では都市部の光害も強いため、実際に「見える・
// 楽しめる」と感じられる距離はもっと近いことが多い。それを一律の係数で
// 織り込む。
//
// 係数の根拠：一般に「5km以上離れると花火のディテールが見えにくくなり、
// 音も遅れて楽しみにくい」「10km以上は小さく見えづらい」とされる（花火
// 鑑賞に関する複数のガイド記事で一致）。この目安に合わせ、尺玉(10号)の
// 理想値20kmが約5〜6kmまで縮むよう0.3を係数にした（単に「見える」ではなく
// 「楽しめる」ラインを表すよう、以前の0.6よりさらに厳しめにした）。
export const HAZE_DISCOUNT_FACTOR = 0.3;

/** 光害・大気の霞みを織り込んだ、現実的な「肉眼で十分見える限界距離」[km]。 */
export function effectiveVisibleLimitKm(shell: ShellSize): [number, number] {
  return [
    shell.visibleLimitKm[0] * HAZE_DISCOUNT_FACTOR,
    shell.visibleLimitKm[1] * HAZE_DISCOUNT_FACTOR,
  ];
}

/**
 * 号数に応じた光の強さの係数(0.5〜1.0)。地上のグラデーション・建物屋上の
 * 光り方の両方で共通に使う。goは2.5号(最小)〜40号(最大)まで16倍の開きが
 * あり線形正規化だと小玉同士の差がほぼ付かないため、対数スケールで正規化する。
 */
export function shellGlowIntensity(shell: ShellSize): number {
  const minGo = SHELL_SIZES[0].go;
  const maxGo = SHELL_SIZES[SHELL_SIZES.length - 1].go;
  const t = (Math.log(shell.go) - Math.log(minGo)) / (Math.log(maxGo) - Math.log(minGo));
  return 0.5 + 0.5 * Math.min(1, Math.max(0, t));
}
